/*!
 * GentlePup theme — product.js
 * Copyright © 2026 Murilo Scigliano. All rights reserved.
 * Proprietary. See LICENSE. dev@murilo.design
 */
/**
 * Keeps the headline price honest with the buy-box selection.
 *
 * The tier radios choose between different products (the trainer alone
 * or a bundle) and the variant select chooses within one, but the big
 * price at the top of the buy box is rendered once for the default.
 * Without this, picking the complete kit leaves the page's biggest
 * number describing the wrong product.
 *
 * Progressive: radios and select post the right variant id without any
 * of this — the sync is a reading aid, not a requirement.
 */
{
  function syncPriceFromControl(source) {
    // The select's price data lives on its <option> elements rather
    // than the control itself, so read the chosen option's dataset.
    const radio = source.classList.contains("product__select")
      ? source.options[source.selectedIndex]
      : source;
    if (!radio?.dataset?.priceNow) return;

    // Scope to the buy column rather than the form: the headline price
    // and the delivery line sit outside the <form> element, so scoping
    // to the form found neither. That is why the delivery line never
    // changed when a bundle was chosen — this function returned at the
    // missing price display before it ever reached the shipping sync.
    const scope =
      source.closest(".product__buy") ||
      source.closest(".product") ||
      source.closest("form") ||
      document;

    // Shipping first, and unconditionally: it must not depend on the
    // price header existing.
    syncShipping(scope, radio);
    syncKit(scope, radio);

    // Every price display in the section follows the choice: the
    // headline in the buy column and the sticky bar outside it. The
    // pricing-story switch decides whether a strikethrough and a SAVE
    // badge appear at all. Liquid marks the headline's badge
    // suppressed, and that one flag governs every display — undoing it
    // once made the badge reappear on load and on every radio change.
    const section = source.closest(".product") || scope;
    const suppressed = section
      .querySelector("[data-price-display] [data-price-save]")
      ?.hasAttribute("data-price-save-suppressed");

    section.querySelectorAll("[data-price-display]").forEach((display) => {
      const now = display.querySelector("[data-price-now]");
      const was = display.querySelector("[data-price-was]");
      const save = display.querySelector("[data-price-save]");

      if (now) now.textContent = radio.dataset.priceNow;

      if (radio.dataset.priceWas && !suppressed) {
        if (was) {
          was.textContent = radio.dataset.priceWas;
          was.hidden = false;
        }
        if (save) {
          save.textContent = radio.dataset.priceSave || "";
          save.hidden = false;
        }
      } else {
        if (was) was.hidden = true;
        if (save) save.hidden = true;
      }
    });
  }

  /**
   * Keeps the delivery line true to the selected option.
   *
   * The threshold sits between the dearest single product and the
   * cheapest bundle, so choosing a bundle is exactly what crosses it —
   * which makes this line part of the decision rather than a footnote.
   * Rendered server-side for the default choice, so with JavaScript off
   * it is still correct for the option checked on load.
   */
  function syncShipping(scope, radio) {
    const line = scope.querySelector(".product__ship");
    if (!line) return;

    const threshold = Number(line.dataset.shipThreshold);
    const cents = Number(radio.dataset.priceCents);
    if (!Number.isFinite(threshold) || !Number.isFinite(cents)) return;

    const text = radio.dataset.shipGapText;
    if (!text) return;

    const isFree = cents >= threshold;
    const wasFree = line.classList.contains("product__ship--free");

    line.textContent = text;
    line.classList.toggle("product__ship--free", isFree);

    // The meter fills toward the threshold as bigger options are
    // picked; CSS owns the width transition, this only sets the target.
    const fill = scope.querySelector("[data-ship-fill]");
    if (fill) {
      // Floor, never round: a selection 40 cents short must not paint a
      // full bar beside a sentence naming the shortfall.
      const pct = isFree ? 100 : Math.min(99, Math.floor((cents / threshold) * 100));
      fill.style.width = pct + "%";
    }

    // Crossing INTO free delivery gets a beat of celebration on the
    // card — the same moment the cart marks. Re-adding the class after
    // a reflow lets the animation replay on repeat crossings.
    if (isFree && !wasFree) {
      const card = line.closest(".product__delivery");
      if (card) {
        card.classList.remove("is-crossing");
        void card.offsetWidth;
        card.classList.add("is-crossing");
      }
    }
  }

  /**
   * Shows the kit that matches the selected option.
   *
   * Every kit view is server-rendered from the Content entries — one
   * per selectable bundle plus the page's own — and this only decides
   * which one is visible. Picking the Ultimate on a duo page reveals
   * its third chip and its own saving; nothing is invented in JS.
   */
  function syncKit(scope, radio) {
    const views = scope.querySelectorAll("[data-kit-view]");
    if (!views.length) return;
    const handle = radio.dataset.productHandle;
    if (!handle) return;
    // A selection with no kit view of its own (e.g. an offered bundle
    // whose Kit content entries are missing) leaves the current kit
    // showing rather than blanking the area.
    let hasView = false;
    views.forEach((view) => {
      if (view.dataset.kitView === handle) hasView = true;
    });
    if (!hasView) return;
    views.forEach((view) => {
      const show = view.dataset.kitView === handle;
      const entering = show && view.hidden;
      view.hidden = !show;
      // A newly revealed kit cascades its chips in — the same beat the
      // delivery strip plays on crossing. Reflow between remove/add
      // lets the animation replay on every swap; CSS owns the motion
      // and the global reduced-motion switch kills it.
      if (entering) {
        view.classList.remove("is-entering");
        void view.offsetWidth;
        view.classList.add("is-entering");
      }
    });
  }

  /**
   * Turns the dispatch and delivery policy into two dates.
   *
   * "5–9 business days from dispatch" asks the reader to do the
   * arithmetic, and a shopper who never works it out cannot be
   * reassured by it. The numbers come from section settings that sit
   * beside the Delivery panel's own copy, so the estimate cannot drift
   * from the promise.
   *
   * Weekends only — public holidays are not modelled, which is why the
   * range is stated as an estimate and the wider end is the one to
   * trust.
   */
  function addBusinessDays(from, days) {
    const date = new Date(from);
    let added = 0;
    while (added < days) {
      date.setDate(date.getDate() + 1);
      const day = date.getDay();
      if (day !== 0 && day !== 6) added += 1;
    }
    return date;
  }

  function renderEta(node) {
    const dispatch = Number(node.dataset.etaDispatchDays);
    const min = Number(node.dataset.etaMinDays);
    const max = Number(node.dataset.etaMaxDays);
    if (![dispatch, min, max].every(Number.isFinite)) return;

    const dispatched = addBusinessDays(new Date(), dispatch);
    const format = new Intl.DateTimeFormat(document.documentElement.lang || "en-AU", {
      weekday: "short",
      day: "numeric",
      month: "short",
    });

    // The dates are the part worth reading, so they are emphasised
    // rather than set in the same weight as the sentence around them.
    // Built as nodes rather than an HTML string: the template comes from
    // a translation file, and inserting that as markup would make every
    // locale a place where markup could be injected.
    const dates = {
      __FROM__: format.format(addBusinessDays(dispatched, min)),
      __TO__: format.format(addBusinessDays(dispatched, max)),
    };

    node.textContent = "";
    for (const part of node.dataset.etaTemplate.split(/(__FROM__|__TO__)/)) {
      if (part in dates) {
        const strong = document.createElement("strong");
        strong.textContent = dates[part];
        node.appendChild(strong);
      } else if (part) {
        node.appendChild(document.createTextNode(part));
      }
    }
    node.hidden = false;
  }

  /**
   * Shows the sticky buy bar once the main add button has scrolled
   * above the viewport, and hides it again while the button is in
   * view, before the shopper has reached it, or once the footer
   * arrives (the bar would otherwise sit on the footer's links).
   *
   * Without IntersectionObserver the bar stays hidden: the in-column
   * button is always there, the bar is only ever a shortcut to it.
   */
  function watchBuyBar() {
    const bar = document.querySelector("[data-buybar]");
    const anchor = document.querySelector(".product__actions");
    if (!bar || !anchor || !("IntersectionObserver" in window)) return;
    if (bar.dataset.watched) return;
    bar.dataset.watched = "1";

    let passed = false;
    let footerNear = false;
    const apply = () => {
      const show = passed && !footerNear;
      if (show && bar.hidden) {
        // Stays display:none until first needed, so the thumbnail is
        // not fetched on a visit that never scrolls past the button.
        // Un-hide one frame before showing, or the slide would not run.
        bar.hidden = false;
        requestAnimationFrame(() => {
          bar.toggleAttribute("data-visible", true);
          bar.inert = false;
        });
        return;
      }
      bar.toggleAttribute("data-visible", show);
      bar.inert = !show;
    };

    // The bar's button mirrors the column's: same label, same disabled
    // state. The form decides availability; the bar only repeats it.
    const main = document.querySelector(".product__submit");
    const mirror = bar.querySelector(".product__buybar-submit");
    if (main && mirror) {
      mirror.disabled = main.disabled;
      const from = main.querySelectorAll(".button-027__text");
      const to = mirror.querySelectorAll(".button-027__text");
      if (from.length && from.length === to.length) {
        to.forEach((el, i) => { el.textContent = from[i].textContent; });
      }
    }

    // The root is stretched far below the viewport, so the button counts
    // as intersecting anywhere at or under the top edge and the only
    // state change is crossing that edge. Observing the bare viewport
    // missed a fast scroll: the button jumped from below to above in one
    // frame without ever intersecting, and the bar never showed. Two
    // crossings can arrive in one delivery, so the LAST entry wins.
    new IntersectionObserver((entries) => {
      passed = !entries[entries.length - 1].isIntersecting;
      apply();
    }, { rootMargin: "0px 0px 100000px 0px" }).observe(anchor);

    const footer = document.querySelector(".site-footer");
    if (footer) {
      new IntersectionObserver((entries) => {
        footerNear = entries[entries.length - 1].isIntersecting;
        apply();
      }).observe(footer);
    }
  }

  // The phone gallery is a snap carousel below 700px (product.css) with
  // a strip of thumbnails under it. The strip ships hidden and is
  // revealed here, so a page whose JS failed shows a plain swipeable
  // slider rather than controls that do nothing.
  function watchGalleryThumbs() {
    const gallery = document.querySelector(".product__gallery");
    const strip = document.querySelector("[data-gallery-thumbs]");
    if (!gallery || !strip) return;
    const thumbs = [...strip.querySelectorAll("[data-gallery-thumb]")];
    const shots = [...gallery.querySelectorAll(".product__shot")];
    if (thumbs.length < 2 || shots.length < 2) return;

    // offsetLeft is measured from the scroller's border box and so
    // includes its inline padding; scrollLeft is measured from the
    // content edge. The two differ by exactly that padding, which is
    // why every shot sat 8px off its own snap point: at rest the first
    // shot reported offsetLeft 8 against scrollLeft 0.
    const originOf = (shot) => shot.offsetLeft - gallery.clientLeft
      - parseFloat(getComputedStyle(gallery).paddingInlineStart || 0);

    const current = () => {
      // At the end of the range the last shot cannot reach its own
      // origin — the scroller runs out of room by the width of the peek
      // — so scrolled fully right IS the last shot, whatever the
      // arithmetic says. Without this the strip marked the second-last.
      if (gallery.scrollLeft >= gallery.scrollWidth - gallery.clientWidth - 2) {
        return shots.length - 1;
      }
      // Otherwise the nearest shot to the scroll position, measured
      // against the shot's own origin rather than an assumed slide
      // width — the slide is a percentage and the gap is fixed, so the
      // two do not divide evenly.
      let best = 0;
      let bestGap = Infinity;
      shots.forEach((shot, i) => {
        const gap = Math.abs(originOf(shot) - gallery.scrollLeft);
        if (gap < bestGap) { bestGap = gap; best = i; }
      });
      return best;
    };

    const sync = () => {
      // Only while the gallery actually scrolls; on the desktop grid
      // there is nothing to track and CSS hides the strip anyway.
      const scrollable = gallery.scrollWidth > gallery.clientWidth + 4;
      strip.hidden = !scrollable;
      if (!scrollable) return;
      const i = current();
      thumbs.forEach((t, n) => {
        const on = n === i;
        t.classList.toggle("is-current", on);
        t.setAttribute("aria-current", on ? "true" : "false");
      });
    };

    thumbs.forEach((thumb, i) => {
      thumb.addEventListener("click", () => {
        const shot = shots[i];
        if (!shot) return;
        const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
        gallery.scrollTo({ left: originOf(shot), behavior: reduced ? "auto" : "smooth" });
      });
    });

    let ticking = false;
    gallery.addEventListener("scroll", () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => { sync(); ticking = false; });
    }, { passive: true });
    window.addEventListener("resize", sync, { passive: true });
    sync();
  }

  document.addEventListener("change", (event) => {
    const source = event.target.closest(".product__tier-input, .product__select");
    if (!source) return;
    syncPriceFromControl(source);
  });

  // Keep the headline price correct if a non-default tier is pre-checked
  // (e.g. single sold out, first available bundle selected server-side).
  document.addEventListener("DOMContentLoaded", () => {
    document.querySelectorAll(".product__tier-input:checked, .product__select").forEach((el) => {
      syncPriceFromControl(el);
    });
    document.querySelectorAll("[data-eta]").forEach(renderEta);
    watchBuyBar();
    watchGalleryThumbs();
  });

  // The theme editor re-renders the section on every setting change and
  // does not re-run a deferred script, so the fresh bar would stay hidden.
  document.addEventListener("shopify:section:load", (event) => {
    if (!event.target?.querySelector?.("[data-buybar]")) return;
    document.querySelectorAll(".product__tier-input:checked, .product__select").forEach((el) => {
      syncPriceFromControl(el);
    });
    watchBuyBar();
    watchGalleryThumbs();
  });
}
